import { useEffect, useState } from 'react'
import { Calendar, CheckCircle2, ListChecks, MapPin, Users } from 'lucide-react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import Modal from '../components/Modal'
import PageHeader from '../components/PageHeader'

interface Event {
  id: number
  title: string
  description: string
  date: string
  location: string | null
  maxAttendees: number | null
  imageUrl: string | null
  visibility: 'ALL' | 'PUBLIC' | 'MEMBERS'
  isActive: boolean
  attendeeCount: number
  isFull: boolean
  attendees: Array<{
    user: {
      id: number
      firstName: string
      lastName: string
      email: string
      beltLevel?: string
      isAdmin?: boolean
    }
  }>
}

export default function Events() {
  const { user } = useAuth()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)
  const [registrations, setRegistrations] = useState<{ title: string; attendees: Array<{ user: { id: number; firstName: string; lastName: string } }>; publicRegistrations: Array<{ id: number; firstName: string; lastName: string }>; withdrawals: Array<{ id: number; firstName: string; lastName: string; reason: string }> } | null>(null)

  const fetchEvents = async () => {
    try {
      setLoading(true)
      const response = await axios.get<Event[]>('/api/events', { params: { includePast: true } })
      setCurrentTime(Date.now())
      setEvents(response.data)
    } catch (error) {
      console.error('Erreur lors du chargement des événements:', error)
      toast.error('Erreur lors du chargement des événements')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [])

  const handleRegister = async (eventId: number) => {
    try {
      await axios.post(`/api/events/${eventId}/register`)
      toast.success('Inscription réussie')
      fetchEvents()
    } catch (error: unknown) {
      const message = axios.isAxiosError<{ message?: string }>(error) ? error.response?.data?.message : undefined
      toast.error(message || 'Erreur lors de l\'inscription')
    }
  }

  const handleUnregister = async (eventId: number) => {
    const reason = window.prompt('Motif du désistement (obligatoire, 1 000 caractères maximum) :')?.trim()
    if (!reason) return
    try {
      await axios.delete(`/api/events/${eventId}/register`, { data: { reason } })
      toast.success('Désinscription réussie')
      fetchEvents()
    } catch (error: unknown) {
      const message = axios.isAxiosError<{ message?: string }>(error) ? error.response?.data?.message : undefined
      toast.error(message || 'Erreur lors de la désinscription')
    }
  }

  const showRegistrations = async (eventId: number) => {
    try {
      const response = await axios.get(`/api/events/${eventId}/registrations`)
      setRegistrations(response.data)
    } catch (error) {
      toast.error(axios.isAxiosError<{ message?: string }>(error) ? error.response?.data?.message || 'Impossible de charger les inscrits' : 'Impossible de charger les inscrits')
    }
  }

  const isUserRegistered = (event: Event) => event.attendees.some((attendee) => attendee.user.id === user?.id)
  const isPastEvent = (event: Event) => new Date(event.date).getTime() < currentTime

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  const formatMonth = (dateString: string) => new Date(dateString)
    .toLocaleDateString('fr-FR', { month: 'short' })
    .replace('.', '')

  const formatTime = (dateString: string) => new Date(dateString).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })

  const orderedEvents = [...events].sort((first, second) => {
    const firstDate = new Date(first.date).getTime()
    const secondDate = new Date(second.date).getTime()
    const firstIsPast = firstDate < currentTime
    const secondIsPast = secondDate < currentTime

    if (firstIsPast !== secondIsPast) return firstIsPast ? 1 : -1
    return firstIsPast ? secondDate - firstDate : firstDate - secondDate
  })

  const renderStatus = (event: Event) => {
    if (!event.isActive) return <span className="event-status event-status-inactive">Inactif</span>
    if (isPastEvent(event)) return <span className="event-status event-status-past">Passé</span>
    if (event.isFull) return <span className="event-status event-status-full">Complet</span>
    return <span className="event-status event-status-upcoming"><CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Inscriptions ouvertes</span>
  }

  const closeModal = () => setSelectedEvent(null)

  const handleModalRegistration = (event: Event, action: 'register' | 'unregister') => {
    if (action === 'register') handleRegister(event.id)
    else handleUnregister(event.id)
    closeModal()
  }

  if (loading) {
    return (
      <div className="empty-state" role="status">
        <div className="mx-auto h-9 w-9 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
        <p className="mt-4 text-sm font-semibold text-gray-600">Chargement des événements…</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <PageHeader
        eyebrow="Agenda du dojo"
        title="Événements du club"
        description="Stages, rencontres et temps forts : retrouvez les prochains rendez-vous et gérez vos inscriptions."
        icon={<Calendar />}
      />

      {events.length === 0 ? (
        <div className="empty-state">
          <Calendar className="mx-auto h-14 w-14 text-gray-300" />
          <h3 className="mt-3 text-lg font-bold text-gray-900">Aucun événement</h3>
          <p className="mt-1 text-sm text-gray-500">Aucun événement n'est prévu pour le moment.</p>
        </div>
      ) : (
        <div className="grid gap-5">
          {orderedEvents.map((event) => {
            const isPast = isPastEvent(event)
            const registered = isUserRegistered(event)

            return (
              <article
                key={event.id}
                className={`card event-card ${isPast ? 'event-card-muted' : ''}`}
                aria-labelledby={`event-title-${event.id}`}
              >
                <div className="relative z-[1] flex flex-col gap-5 md:flex-row md:items-start">
                  <div className="event-date-tile" aria-label={formatDate(event.date)}>
                    <span>{formatMonth(event.date)}</span>
                    <strong>{new Date(event.date).getDate()}</strong>
                    <small>{formatTime(event.date)}</small>
                  </div>

                  <div className="min-w-0 flex-1">
                    {event.imageUrl && <img src={event.imageUrl} alt="" className="mb-4 max-h-64 w-full rounded-2xl object-cover" />}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="eyebrow !mb-1">Rendez-vous du club</p>
                        <h3 id={`event-title-${event.id}`} className="text-xl font-extrabold text-gray-900">{event.title}</h3>
                      </div>
                      <div className="flex flex-wrap gap-1.5">{renderStatus(event)}</div>
                    </div>

                    <div className="event-meta mt-3">
                      <span><Calendar aria-hidden="true" /> {formatDate(event.date)}</span>
                      {event.location && <span><MapPin aria-hidden="true" /> {event.location}</span>}
                      <span>
                        <Users aria-hidden="true" /> {event.attendeeCount} inscrit{event.attendeeCount !== 1 ? 's' : ''}
                        {event.maxAttendees ? ` / ${event.maxAttendees}` : ''}
                      </span>
                    </div>

                    <p className="mt-4 line-clamp-2 text-sm leading-6 text-gray-700">{event.description}</p>

                    <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-primary-900/10 pt-4">
                      <button type="button" onClick={() => setSelectedEvent(event)} className="btn-secondary">
                        Voir les détails
                      </button>
                      {(user?.isAdmin || user?.role === 'SENSEI') && <button type="button" onClick={() => void showRegistrations(event.id)} className="btn-secondary"><ListChecks className="h-4 w-4" /> Voir les inscrits</button>}
                      {event.isActive && !isPast && user && (
                        registered ? (
                          <button type="button" onClick={() => handleUnregister(event.id)} className="btn-danger">
                            Se désinscrire
                          </button>
                        ) : !event.isFull ? (
                          <button type="button" onClick={() => handleRegister(event.id)} className="btn-primary">
                            S'inscrire
                          </button>
                        ) : null
                      )}
                    </div>

                    <div className="mt-3 grid gap-2">
                      {event.isFull && <div className="event-notice event-notice-danger">Cet événement est complet.</div>}
                      {isPast && <div className="event-notice event-notice-neutral">Cet événement est passé.</div>}
                      {!event.isActive && <div className="event-notice event-notice-neutral">Cet événement n'est plus actif.</div>}
                    </div>
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}

      {selectedEvent && (
        <Modal
          isOpen
          onClose={closeModal}
          title={selectedEvent.title}
          eyebrow="Détail de l’événement"
          size="md"
          footer={(
            <>
              {selectedEvent.isActive && !isPastEvent(selectedEvent) && user && (
                isUserRegistered(selectedEvent) ? (
                  <button
                    type="button"
                    onClick={() => handleModalRegistration(selectedEvent, 'unregister')}
                    className="btn-danger"
                  >
                    Se désinscrire
                  </button>
                ) : !selectedEvent.isFull ? (
                  <button
                    type="button"
                    onClick={() => handleModalRegistration(selectedEvent, 'register')}
                    className="btn-primary"
                  >
                    S'inscrire
                  </button>
                ) : null
              )}
              <button type="button" onClick={closeModal} className="btn-secondary">Fermer</button>
            </>
          )}
        >
          <div className="space-y-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <div className="event-date-tile" aria-label={formatDate(selectedEvent.date)}>
                <span>{formatMonth(selectedEvent.date)}</span>
                <strong>{new Date(selectedEvent.date).getDate()}</strong>
                <small>{formatTime(selectedEvent.date)}</small>
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-3 flex flex-wrap gap-1.5">{renderStatus(selectedEvent)}</div>
                <div className="event-meta">
                  <span><Calendar aria-hidden="true" /> {formatDate(selectedEvent.date)}</span>
                  {selectedEvent.location && <span><MapPin aria-hidden="true" /> {selectedEvent.location}</span>}
                  <span>
                    <Users aria-hidden="true" /> {selectedEvent.attendeeCount} inscrit{selectedEvent.attendeeCount !== 1 ? 's' : ''}
                    {selectedEvent.maxAttendees ? ` sur ${selectedEvent.maxAttendees}` : ''}
                  </span>
                </div>
              </div>
            </div>

            <section className="modal-section p-4 sm:p-5" aria-labelledby="event-description-title">
              <h3 id="event-description-title" className="text-lg font-extrabold text-gray-900">À propos de l'événement</h3>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-7 text-gray-700">{selectedEvent.description}</p>
            </section>

            <div className="grid gap-2">
              {isUserRegistered(selectedEvent) && selectedEvent.isActive && !isPastEvent(selectedEvent) && (
                <div className="event-notice event-status-upcoming flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Vous êtes inscrit à cet événement.
                </div>
              )}
              {selectedEvent.isFull && <div className="event-notice event-notice-danger">Cet événement est complet.</div>}
              {isPastEvent(selectedEvent) && <div className="event-notice event-notice-neutral">Cet événement est passé.</div>}
              {!selectedEvent.isActive && <div className="event-notice event-notice-neutral">Les inscriptions sont fermées.</div>}
            </div>
          </div>
        </Modal>
      )}
      <Modal isOpen={registrations !== null} onClose={() => setRegistrations(null)} title={registrations?.title ?? 'Inscriptions'} eyebrow="Suivi de l’événement" size="md">
        {registrations && <div className="space-y-5">
          <section className="modal-section p-4"><h3 className="font-bold text-gray-900">Adhérents ({registrations.attendees.length})</h3><div className="mt-2 space-y-1">{registrations.attendees.map(item => <p key={item.user.id} className="text-sm text-gray-700">{item.user.firstName} {item.user.lastName}</p>)}</div></section>
          <section className="modal-section p-4"><h3 className="font-bold text-gray-900">Visiteurs ({registrations.publicRegistrations.length})</h3><div className="mt-2 space-y-1">{registrations.publicRegistrations.map(item => <p key={item.id} className="text-sm text-gray-700">{item.firstName} {item.lastName}</p>)}</div></section>
          <section className="modal-section p-4"><h3 className="font-bold text-gray-900">Désistements ({registrations.withdrawals.length})</h3><div className="mt-2 space-y-3">{registrations.withdrawals.map(item => <div key={item.id}><p className="text-sm font-bold text-gray-800">{item.firstName} {item.lastName}</p><p className="text-sm text-gray-600">{item.reason}</p></div>)}</div></section>
        </div>}
      </Modal>
    </div>
  )
}
